import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
  errorMessage,
  onboardingStepId,
  speciesSubsteps,
} from '@helpers/Constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@contexts/Toast';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import {
  FeetypeProps,
  ServiceProps,
  ServiceRateFeetypeSelection,
  ServiceRateServiceSelection,
  Species,
  Service,
} from '@helpers/Models';
import { Assets } from '@helpers/Constants/Assets';
import { ButcherHeader } from '@components/ButcherHeader';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { FeeTypeBottomSheet } from '@components/BottomSheet/FeeTypeBottomSheet';
import { ServiceBottomSheet } from '@components/BottomSheet/ServiceBottomSheet';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';

interface ActiveRow {
  speciesId: number;
  serviceKey: string | number;
  field: 'service' | 'feeType';
}

interface ValidationErrors {
  [speciesId: number]: {
    [serviceIndex: number]: {
      serviceId?: string;
      feeTypeId?: string;
      price?: string;
      duplicate?: string;
    };
  };
}

// ---- Helper to clean species data for comparison (ignores temp fields) ----
const cleanSpeciesForComparison = (speciesData: Species[]): any => {
  return speciesData.map((species) => ({
    ...species,
    services: species.services
      .filter((service) => !service.deletedAt)
      .map((service) => {
        const { _tempId, isNew, ...rest } = service as any;
        return rest;
      }),
  }));
};

// ---- Helper to count added and updated services ----
const countServiceChanges = (current: Species[], initial: Species[]) => {
  let added = 0;
  let updated = 0;

  const initialServicesMap = new Map<number, Service>();
  initial.forEach((species) => {
    species.services.forEach((service) => {
      if (service.id && !service.deletedAt) {
        initialServicesMap.set(service.id, service);
      }
    });
  });

  current.forEach((species) => {
    species.services.forEach((service) => {
      if (service.deletedAt) return;
      if (!service.id) {
        added++;
      } else {
        const initialService = initialServicesMap.get(service.id);
        if (initialService) {
          const { id, deletedAt, _tempId, isNew, ...currentFields } = service as any;
          const { id: initId, deletedAt: initDeleted, _tempId: initTemp, isNew: initNew, ...initialFields } = initialService as any;
          if (JSON.stringify(currentFields) !== JSON.stringify(initialFields)) {
            updated++;
          }
        } else {
          added++;
        }
      }
    });
  });

  return { added, updated };
};

export const SpeciesAndService: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const { updateOnboardingStep } = useLocalStorage();

  const [speciesData, setSpeciesData] = useState<Species[]>([]);
  const [serviceOptions, setServiceOptions] = useState<ServiceProps[]>([]);
  const [feeTypeOptions, setFeeTypeOptions] = useState<FeetypeProps[]>([]);

  const [isFeetypeSheetOpen, setIsFeetypeSheetOpen] = useState(false);
  const [isServiceSheetOpen, setIsServiceSheetOpen] = useState(false);
  const [activeRow, setActiveRow] = useState<ActiveRow | null>(null);

  const location = useLocation();
  const fromSettings = location.state?.fromSettings;
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [initialList, setInitialList] = useState<Species[]>([]);
  const [tempIdCounter, setTempIdCounter] = useState(-1);
  const [totalErrors, setTotalErrors] = useState(0);
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});

  const [navigateTo, setNavigateTo] = useState<string | null>(null);

  // ---- Derive unsaved changes using cleaned data ----
  useEffect(() => {
    const cleanedCurrent = cleanSpeciesForComparison(speciesData);
    const cleanedInitial = cleanSpeciesForComparison(initialList);
    const changed = JSON.stringify(cleanedCurrent) !== JSON.stringify(cleanedInitial);
    setHasUnsavedChanges(changed);
  }, [speciesData, initialList]);

  useUnsavedChangesPrompt(hasUnsavedChanges, undefined, 'Leave Without Saving');

  // ---- Compute total error count from validationErrors ----
  useEffect(() => {
    let count = 0;
    for (const speciesId in validationErrors) {
      for (const index in validationErrors[speciesId]) {
        const errorObj = validationErrors[speciesId][index];
        if (Object.keys(errorObj).length > 0) {
          count++;
        }
      }
    }
    setTotalErrors(count);
  }, [validationErrors]);

  // Navigate when unsaved changes are cleared and a route is pending
  useEffect(() => {
    if (navigateTo && !hasUnsavedChanges) {
      if (fromSettings) {
        navigate(navigateTo, { state: { fromSettings } });
      } else {
        navigate(navigateTo);
      }
      setNavigateTo(null);
    }
  }, [navigateTo, hasUnsavedChanges, navigate, fromSettings]);

  useEffect(() => {
    fetchSpeciesAndServiceInfo();
  }, []);

  useEffect(() => {
    if (location.state?.colorStepSkipped) {
      showToastMessage(
        'Color step is skipped because none of your services have color flag enabled',
        'success'
      );
      window.history.replaceState(
        { ...location.state, colorStepSkipped: false },
        '',
        location.pathname
      );
    }
  }, [location.state?.colorStepSkipped, location.pathname, location.state]);

  useEffect(() => {
    fetchFeeTypes();
  }, [isFeetypeSheetOpen]);

  // ---- Data fetching ----
  const fetchSpeciesAndServiceInfo = async (): Promise<void> => {
    try {
      const speciesResponse = await Axios.get(ApiEndpoints.FETCH_SERVICE_RATE);
      const serviceOptionsResponse = await Axios.post(
        ApiEndpoints.FETCH_SERVICE,
        {}
      );

      const speciesData = speciesResponse.data.data.map((species: Species) => ({
        ...species,
        services: species.services ?? [],
      }));

      setSpeciesData(speciesData);
      setInitialList(speciesData);
      setServiceOptions(serviceOptionsResponse.data.data.services);
      setValidationErrors({});
      setPriceDrafts({});
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_SPECIES_AND_SERVICE, 'error');
    }
  };

  const fetchFeeTypes = async (): Promise<void> => {
    try {
      const feeTypeResponse = await Axios.post(ApiEndpoints.FETCH_FEETYPE, {});
      setFeeTypeOptions(feeTypeResponse.data.data.feeTypes);
    } catch {
      // silent
    }
  };

  // ---- Validation (now allows price = 0) ----
  const validateAllServices = (): boolean => {
    const errors: ValidationErrors = {};
    let hasError = false;

    speciesData.forEach((species) => {
      const serviceMap = new Map<string, number>();

      species.services.forEach((service, index) => {
        if (service.deletedAt) return;

        const serviceErrors: { [key: string]: string } = {};

        if (!service.serviceId || service.serviceId === 0) {
          serviceErrors.serviceId = errorMessage.FORM_REQUIRED;
        }
        if (!service.serviceFeeType || service.serviceFeeType === 0) {
          serviceErrors.feeTypeId = errorMessage.FORM_REQUIRED;
        }
        // Allow 0, but reject negative or NaN
        if (isNaN(service.serviceRate) || service.serviceRate < 0) {
          serviceErrors.price = 'Price must be a non-negative number';
        }

        if (service.serviceId && service.serviceFeeType) {
          const mapKey = `${service.serviceId}-${service.serviceFeeType}`;
          if (serviceMap.has(mapKey)) {
            const duplicateIndex = serviceMap.get(mapKey)!;
            if (!errors[species.id]) errors[species.id] = {};
            errors[species.id][index] = {
              ...errors[species.id]?.[index],
              duplicate: errorMessage.DUPLICATE_SERVICE,
            };
            errors[species.id][duplicateIndex] = {
              ...errors[species.id]?.[duplicateIndex],
              duplicate: errorMessage.DUPLICATE_SERVICE,
            };
            hasError = true;
          } else {
            serviceMap.set(mapKey, index);
          }
        }

        if (Object.keys(serviceErrors).length > 0) {
          if (!errors[species.id]) errors[species.id] = {};
          errors[species.id][index] = {
            ...errors[species.id]?.[index],
            ...serviceErrors,
          };
          hasError = true;
        }
      });
    });

    setValidationErrors(errors);
    return !hasError;
  };

  // ---- Helper: check if any service exists across all species ----
  const hasAnyService = (): boolean => {
    return speciesData.some((species) =>
      species.services.some((service) => !service.deletedAt)
    );
  };

  // ---- Helper: check if a species has any incomplete service (allows price 0) ----
  const hasIncompleteService = (speciesId: number): boolean => {
    const species = speciesData.find((s) => s.id === speciesId);
    if (!species) return false;
    return species.services.some((service) => {
      if (service.deletedAt) return false;
      // Check required fields: serviceId, serviceFeeType, and price (must be >= 0, not NaN)
      return (
        !service.serviceId ||
        service.serviceId === 0 ||
        !service.serviceFeeType ||
        service.serviceFeeType === 0 ||
        isNaN(service.serviceRate) ||
        service.serviceRate < 0
      );
    });
  };

  // ---- Update existing row ----
  const updateServiceField = (
    speciesId: number,
    serviceKey: string | number,
    field: 'serviceId' | 'serviceFeeType' | 'serviceRate' | 'taxableFlag',
    value: any
  ) => {
    setSpeciesData((prev) =>
      prev.map((species) =>
        species.id === speciesId
          ? {
            ...species,
            services: species.services.map((service) => {
              const key = service.id || (service as any)._tempId;
              return key === serviceKey
                ? { ...service, [field]: value }
                : service;
            }),
          }
          : species
      )
    );
  };

  const getPriceDraftKey = (speciesId: number, serviceKey: string | number): string => {
    return `${speciesId}-${serviceKey}`;
  };

  const handlePriceChange = (
    speciesId: number,
    serviceKey: string | number,
    rawValue: string
  ): void => {
    const draftKey = getPriceDraftKey(speciesId, serviceKey);
    setPriceDrafts((prev) => ({ ...prev, [draftKey]: rawValue }));

    if (rawValue === '') {
      updateServiceField(speciesId, serviceKey, 'serviceRate', Number.NaN);
      return;
    }

    const parsedValue = Number(rawValue);
    if (!Number.isNaN(parsedValue)) {
      updateServiceField(speciesId, serviceKey, 'serviceRate', parsedValue);
    }
  };

  const handlePriceBlur = (
    speciesId: number,
    serviceKey: string | number
  ): void => {
    const draftKey = getPriceDraftKey(speciesId, serviceKey);
    const rawValue = priceDrafts[draftKey];

    if (rawValue === undefined) {
      return;
    }

    if (rawValue.trim() === '') {
      return;
    }

    const parsedValue = Number(rawValue);
    if (!Number.isNaN(parsedValue)) {
      updateServiceField(speciesId, serviceKey, 'serviceRate', parsedValue);
    }

    setPriceDrafts((prev) => {
      const next = { ...prev };
      delete next[draftKey];
      return next;
    });
  };

  // ---- Bottom sheet selections for existing rows ----
  const handleSelectServiceForRow = (service: ServiceRateServiceSelection) => {
    if (activeRow) {
      updateServiceField(
        activeRow.speciesId,
        activeRow.serviceKey,
        'serviceId',
        service.id
      );
      setActiveRow(null);
      setIsServiceSheetOpen(false);
    }
  };

  const handleSelectFeeTypeForRow = (feeType: ServiceRateFeetypeSelection) => {
    if (activeRow) {
      updateServiceField(
        activeRow.speciesId,
        activeRow.serviceKey,
        'serviceFeeType',
        feeType.id
      );
      setActiveRow(null);
      setIsFeetypeSheetOpen(false);
    }
  };

  // ---- Add new service (unsaved) ----
  const handleAddNewService = (speciesId: number) => {
    // Check if there is any incomplete service for this species
    if (hasIncompleteService(speciesId)) {
      showToastMessage(
        'Complete the existing service before creating a new one.',
        'error'
      );
      return;
    }

    const newTempId = tempIdCounter;
    setTempIdCounter((prev) => prev - 1);

    const newService = {
      serviceId: 0,
      serviceFeeType: 0,
      serviceRate: 0,
      taxableFlag: 0,
      deletedAt: '',
      _tempId: newTempId,
    };

    setSpeciesData((prev) =>
      prev.map((species) =>
        species.id === speciesId
          ? {
            ...species,
            services: [...species.services, newService as any],
          }
          : species
      ) as Species[]
    );

    setTimeout(() => {
      const scrollArea = document.getElementById('service-table-scroll-area');
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      }
    }, 100);
  };

  // ---- Delete (handles both saved and unsaved) ----
  const performDelete = async (
    speciesId: number,
    serviceKey: string | number,
    dbId?: number
  ): Promise<void> => {
    if (dbId) {
      try {
        await Axios.delete(ApiEndpoints.DELETE_SERVICE_RATE, {
          data: { ids: [dbId] },
        });
        showToastMessage('Service deleted successfully', 'success');
        await fetchSpeciesAndServiceInfo();
      } catch (error) {
        const extractApiMessage = (payload: unknown): string | undefined => {
          if (!payload) return undefined;

          if (Array.isArray(payload)) {
            for (const item of payload) {
              const nested = extractApiMessage(item);
              if (nested) return nested;
            }
            return undefined;
          }

          if (typeof payload === 'object' && payload !== null) {
            const candidate = payload as {
              message?: unknown;
              errors?: unknown;
              data?: unknown;
            };

            if (
              typeof candidate.message === 'string' &&
              candidate.message.trim()
            ) {
              return candidate.message;
            }

            const fromErrors = extractApiMessage(candidate.errors);
            if (fromErrors) return fromErrors;

            const fromData = extractApiMessage(candidate.data);
            if (fromData) return fromData;

            for (const value of Object.values(candidate)) {
              const nested = extractApiMessage(value);
              if (nested) return nested;
            }
          }

          return undefined;
        };

        const statusCode =
          (error as {
            response?: {
              status?: number;
              data?: unknown;
            };
          })?.response?.status;

        if (statusCode === 400) {
          const responseData = (error as {
            response?: {
              data?: unknown;
            };
          })?.response?.data;

          const apiMessage = extractApiMessage(responseData);
          if (apiMessage) {
            showToastMessage(apiMessage, 'error');
            return;
          }
        }

        showToastMessage(errorMessage.DELETE_FAILED, 'error');
      }
      return;
    }

    // Unsaved row - remove directly from local state
    setSpeciesData((prev) =>
      prev.map((species) =>
        species.id === speciesId
          ? {
            ...species,
            services: species.services.filter((service) => {
              const key = service.id || (service as any)._tempId;
              return key !== serviceKey;
            }),
          }
          : species
      )
    );
  };

  const handleDelete = (
    speciesId: number,
    serviceKey: string | number,
    dbId?: number
  ): void => {
    showConfirmationToast(
      'Are you sure you want to delete this item?',
      'Delete Confirmation',
      'delete',
      () => performDelete(speciesId, serviceKey, dbId),
      'Delete',
      undefined,
      'Cancel'
    );
  };

  // ---- Submit ----
  const handleSubmit: (
    event: React.FormEvent,
    nextPage: boolean,
    addMore: boolean
  ) => Promise<void> = async (event, nextPage, addMore) => {
    event.preventDefault();

    if (!hasAnyService()) {
      showToastMessage(
        'Please add at least one service to a species before proceeding.',
        'error'
      );
      return;
    }

    const isValid = validateAllServices();
    if (!isValid) {
      const errorMsg = totalErrors > 0
        ? `Please fix ${totalErrors} validation ${totalErrors > 1 ? 'errors' : 'error'} before saving.`
        : 'Please fix validation errors before saving.';
      showToastMessage(errorMsg, 'error');
      return;
    }

    const cleanedCurrent = cleanSpeciesForComparison(speciesData);
    const cleanedInitial = cleanSpeciesForComparison(initialList);
    const isDataModified = JSON.stringify(cleanedCurrent) !== JSON.stringify(cleanedInitial);

    if (!isDataModified) {
      if (nextPage) {
        setHasUnsavedChanges(false);
        setNavigateTo(FrontendRoutes.PRODUCTS);
        return;
      } else if (addMore) {
        setHasUnsavedChanges(false);
        setNavigateTo(FrontendRoutes.SPECIES);
        return;
      }
      showToastMessage('No changes to save.', 'success');
      return;
    }

    // ---- Handle restoration of deleted services ----
    // Find all new services (no id) that match a deleted service (same speciesId, serviceId, feeType)
    const restoreIds: number[] = [];
    const updatedSpeciesData = speciesData.map((species) => {
      const updatedServices = species.services.map((service) => {
        if (service.id || service.deletedAt) return service; // only new non-deleted
        // Check if there is a deleted service with same speciesId, serviceId, feeType
        const deletedMatch = initialList
          .find(s => s.id === species.id)
          ?.services.find(
            (s) =>
              s.deletedAt &&
              s.serviceId === service.serviceId &&
              s.serviceFeeType === service.serviceFeeType
          );
        if (deletedMatch) {
          restoreIds.push(deletedMatch.id!);
          // Create a restored service without temp fields and with deletedAt empty string
          const { _tempId, isNew, ...rest } = deletedMatch as any;
          return {
            ...rest,
            serviceRate: service.serviceRate,
            taxableFlag: service.taxableFlag,
            deletedAt: "", // set to empty string to indicate not deleted
          };
        }
        return service;
      });
      return { ...species, services: updatedServices };
    });

    // Call restore API if any
    if (restoreIds.length > 0) {
      try {
        await Axios.post(ApiEndpoints.RESTORE_SERVICE_RATE, { ids: restoreIds });
        // After restoration, we should refetch or update state? The main save will update.
      } catch (error) {
        showToastMessage('Failed to restore some services.', 'error');
        return;
      }
    }

    // Update speciesData with the restored services (so that payload includes ids)
    setSpeciesData(updatedSpeciesData);
    // Now construct payload from updatedSpeciesData
    const payload = updatedSpeciesData.map((species) => ({
      ...(species.id && { id: species.id }),
      speciesName: species.speciesName,
      services: species.services
        .filter((s) => !s.deletedAt)
        .map(({ id, taxableFlag, ...service }) => {
          const { _tempId, isNew, ...cleanService } = service as any;
          return {
            ...(id && { id }),
            ...cleanService,
            taxableFlag: taxableFlag ? 1 : 0,
          };
        }),
    }));

    const { added, updated } = countServiceChanges(updatedSpeciesData, initialList);

    try {
      await Axios.post(ApiEndpoints.CREATE_SERVICE_RATE, payload);

      let successMessage = '';
      if (added > 0 && updated > 0) {
        successMessage = `${added} service${added > 1 ? 's' : ''} added and ${updated} service${updated > 1 ? 's' : ''} updated successfully`;
      } else if (added > 0) {
        successMessage = `${added} service${added > 1 ? 's' : ''} added successfully`;
      } else if (updated > 0) {
        successMessage = `${updated} service${updated > 1 ? 's' : ''} updated successfully`;
      } else {
        successMessage = 'Services saved successfully';
      }
      showToastMessage(successMessage, 'success');

      updateOnboardingStep(onboardingStepId.SPECIES_AND_SERVICE);
      await fetchSpeciesAndServiceInfo();

      if (nextPage) {
        setNavigateTo(FrontendRoutes.PRODUCTS);
      } else if (addMore) {
        setNavigateTo(FrontendRoutes.SPECIES);
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  // ---- Search ----
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchKey(e.target.value);
  };

  const filteredSpeciesData = searchKey.trim()
    ? speciesData.filter((species) =>
      species.speciesName
        .toLowerCase()
        .includes(searchKey.toLowerCase().trim())
    )
    : speciesData;

  // ---- UI ----
  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Species"
          activeStep={5}
          subNavSteps={speciesSubsteps}
          fromSettings={fromSettings ? true : false}
          forStoreSetting={false}
        />
      </div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="font-semibold text-lg">Species and Services</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search species"
              value={searchKey}
              onChange={handleSearchChange}
              className="border border-gray-300 rounded p-2 pl-3 pr-10 w-80"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" id="service-table-scroll-area">
        {filteredSpeciesData.map((species) => (
          <div
            key={species.id}
            className="p-6 rounded-lg border border-gray-300 mb-6"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{species.speciesName}</h2>
              <button
                type="button"
                onClick={() => handleAddNewService(species.id)}
                className="py-2 px-6 text-[13px] font-semibold bg-primary text-white border border-primary rounded-md hover:bg-white hover:text-primary transition-colors duration-200"
              >
                + Add Services
              </button>
            </div>

            <div className="flex items-center w-full bg-gray-100 py-3 px-4 mb-4 font-semibold">
              <div className="flex-1 flex items-center min-w-0">
                Service
                <img
                  src={Assets.SORT_ICON}
                  alt="icon"
                  className="w-3 h-3 ml-2"
                />
              </div>
              <div className="flex-[2] min-w-[180px] text-center">Fee Type</div>
              <div className="flex-1 min-w-[120px] text-center">Price</div>
              <div className="flex-shrink-0 w-20 text-center">Taxable</div>
              <div className="flex-shrink-0 w-16 text-center">Actions</div>
            </div>

            {species.services.map((service, index) => {
              if (service.deletedAt) return null;

              const serviceKey = service.id || (service as any)._tempId;
              const errorsForRow = validationErrors[species.id]?.[index] || {};

              return (
                <div
                  key={serviceKey}
                  className="flex flex-col w-full mb-4 pb-2"
                >
                  <div className="flex items-center w-full">
                    <div className="flex-1 min-w-0 pr-4">
                      <button
                        type="button"
                        className={`w-full p-2 border rounded flex justify-between items-center cursor-pointer ${errorsForRow.serviceId ? 'border-red-500' : 'border-gray-300'
                          }`}
                        onClick={() => {
                          setActiveRow({
                            speciesId: species.id,
                            serviceKey,
                            field: 'service',
                          });
                          setIsServiceSheetOpen(true);
                        }}
                      >
                        <span
                          className="truncate max-w-[100%] overflow-hidden"
                          title={
                            serviceOptions.find(
                              (opt) => opt.id === service.serviceId
                            )?.name || 'Select Service'
                          }
                        >
                          {serviceOptions.find(
                            (opt) => opt.id === service.serviceId
                          )?.name || 'Select Service'}
                        </span>
                        <svg
                          width="15"
                          height="8"
                          viewBox="0 0 15 8"
                          fill="none"
                        >
                          <path
                            d="M2 1.5L7.5 6.5L13 1.5"
                            stroke="#A09CAB"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="flex-[2] min-w-[180px] pr-4 flex justify-center">
                      <button
                        type="button"
                        className={`w-full p-2 border rounded flex justify-between items-center cursor-pointer ${errorsForRow.feeTypeId ? 'border-red-500' : 'border-gray-300'
                          }`}
                        onClick={() => {
                          setActiveRow({
                            speciesId: species.id,
                            serviceKey,
                            field: 'feeType',
                          });
                          setIsFeetypeSheetOpen(true);
                        }}
                      >
                        <span
                          className="truncate overflow-hidden"
                          title={
                            feeTypeOptions.find(
                              (fee) => fee.id === service.serviceFeeType
                            )?.feeType || 'Select Fee Type'
                          }
                        >
                          {feeTypeOptions.find(
                            (fee) => fee.id === service.serviceFeeType
                          )?.feeType || 'Select Fee Type'}
                        </span>
                        <svg
                          width="15"
                          height="8"
                          viewBox="0 0 15 8"
                          fill="none"
                        >
                          <path
                            d="M2 1.5L7.5 6.5L13 1.5"
                            stroke="#A09CAB"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="flex-1 min-w-[120px] pr-4 flex items-center justify-center">
                      <span className="mr-1 text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        className={`w-16 p-2 border rounded focus:outline-primary text-center ${errorsForRow.price ? 'border-red-500' : 'border-gray-300'
                          }`}
                        value={
                          priceDrafts[getPriceDraftKey(species.id, serviceKey)] ??
                          (Number.isNaN(service.serviceRate) ? '' : service.serviceRate)
                        }
                        onChange={(e) =>
                          handlePriceChange(species.id, serviceKey, e.target.value)
                        }
                        onBlur={() => handlePriceBlur(species.id, serviceKey)}
                      />
                      {(() => {
                        const selectedFee = feeTypeOptions.find(
                          (fee) => fee.id === service.serviceFeeType
                        );
                        if (selectedFee?.feeType === 'Flat Fee') {
                          return <span className="w-8" />;
                        }
                        return (
                          <span className="ml-1 text-gray-500 text-sm">
                            / {selectedFee?.unit ?? '-'}
                          </span>
                        );
                      })()}
                    </div>

                    <div className="flex-shrink-0 w-20 flex justify-center items-center">
                      <input
                        type="checkbox"
                        checked={Boolean(service.taxableFlag)}
                        onChange={(e) =>
                          updateServiceField(
                            species.id,
                            serviceKey,
                            'taxableFlag',
                            e.target.checked ? 1 : 0
                          )
                        }
                        className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex-shrink-0 w-16 flex gap-2 justify-center">
                      <button
                        type="button"
                        onClick={() =>
                          handleDelete(species.id, serviceKey, service.id)
                        }
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </div>
                  </div>

                  {Object.keys(errorsForRow).length > 0 && (
                    <div className="w-full mt-1 text-red-500 text-sm">
                      {errorsForRow.serviceId && (
                        <p>{errorMessage.FORM_REQUIRED}</p>
                      )}
                      {errorsForRow.feeTypeId && (
                        <p>{errorMessage.FORM_REQUIRED}</p>
                      )}
                      {errorsForRow.price && <p>{errorsForRow.price}</p>}
                      {errorsForRow.duplicate && (
                        <p>{errorMessage.DUPLICATE_SERVICE}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {speciesData.length === 0 && (
          <div className="flex items-center justify-center h-full w-full">
            No data available.
          </div>
        )}
        {speciesData.length > 0 && filteredSpeciesData.length === 0 && (
          <div className="flex items-center justify-center h-full w-full">
            No species found matching &ldquo;{searchKey}&rdquo;.
          </div>
        )}
      </div>

      {/* Bottom sheets (portals) */}
      {isServiceSheetOpen &&
        ReactDOM.createPortal(
          <ServiceBottomSheet
            onClose={() => setIsServiceSheetOpen(false)}
            onSelect={(service) => {
              if (activeRow) {
                handleSelectServiceForRow(service);
              }
            }}
          />,
          document.body
        )}

      {isFeetypeSheetOpen &&
        ReactDOM.createPortal(
          <FeeTypeBottomSheet
            onClose={() => setIsFeetypeSheetOpen(false)}
            onSelect={(feeType) => {
              if (activeRow) {
                handleSelectFeeTypeForRow(feeType);
              }
            }}
          />,
          document.body
        )}

      <div className="sticky bottom-0 bg-white border-t border-gray-10 w-full mt-3">
        <div className="w-full py-6">
          <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-end gap-4 w-full">
            <button
              type="button"
              onClick={(e) => {
                // If there are unsaved changes, show confirmation before proceeding
                if (hasUnsavedChanges) {
                  showConfirmationToast(
                    'You have unsaved changes. Do you want to save before adding more?',
                    'Unsaved Changes',
                    'info',
                    () => {
                      // Save and add more
                      handleSubmit(e as any, false, true);
                    },
                    'Save and Add More',
                    () => {
                      // Discard changes and add more
                      setSpeciesData(initialList);
                      setHasUnsavedChanges(false);
                      setNavigateTo(FrontendRoutes.SPECIES);
                    },
                    'Discard and Add More'
                  );
                } else {
                  // No changes, just navigate
                  setNavigateTo(FrontendRoutes.SPECIES);
                }
              }}
              className="w-full 2xl:w-[150px] h-8 px-6 flex items-center justify-center space-x-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              <span>+ Add More</span>
            </button>

            <div className="w-full 2xl:w-auto flex items-center justify-between 2xl:justify-end gap-4">
              <button
                type="button"
                onClick={(e) => handleSubmit(e, false, false)}
                disabled={!hasUnsavedChanges}
                className={`w-1/2 2xl:w-[130px] h-8 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasUnsavedChanges
                  ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                  : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
                  }`}
              >
                Save
              </button>

              <button
                type="button"
                onClick={(e) => handleSubmit(e, true, false)}
                disabled={hasUnsavedChanges}
                className={`w-1/2 2xl:w-[150px] h-8 px-6 border rounded-md text-sm font-medium flex items-center justify-center space-x-2 transition-all duration-200 ${hasUnsavedChanges
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300'
                  : 'bg-primary text-white hover:bg-dark-primary border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                  }`}
              >
                <span>Next</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-full max-w-[1440px] p-4 sm:p-6 lg:p-8">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} speciesSetup={true} />
      )}
    </>
  );
};